/**
 * VendorHealthCard — iter85.5 (Release Ops → Phase 1 · informational)
 *
 * Operational vendor rollup before a deploy: contract renewal radar,
 * Resend sending-domain verification, API-Sports request quotas
 * (API-Football soccer + API-NFL american football — separate
 * subscriptions on the same account), Slash Golf quota.
 * On-demand only (each run costs 1 API-Sports request).
 *
 * iter86.31.10 — Redesigned as a CollapsibleCard (matching the
 * Obligations Engine card above it) so the verdict drives the outline
 * color (green on pass) and the check list collapses behind the header.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Loader2, Activity, CheckCircle2, AlertTriangle, XCircle, MinusCircle } from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { CopyDiagButton, buildDiagText, diagLine } from "@/components/site-admin/release-ops/CopyDiagButton";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";

const STATUS_META = {
    pass: { icon: CheckCircle2, cls: "text-emerald-400" },
    warn: { icon: AlertTriangle, cls: "text-amber-400" },
    fail: { icon: XCircle, cls: "text-red-400" },
    skipped: { icon: MinusCircle, cls: "text-zinc-500" },
};

const OVERALL_PILL = {
    pass: "bg-emerald-900/40 text-emerald-200 border-emerald-700",
    warn: "bg-amber-900/40 text-amber-200 border-amber-700",
    fail: "bg-rose-900/40 text-rose-200 border-rose-700",
};

export default function VendorHealthCard({ onVerdictResolved }) {
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const autoRan = useRef(false);

    const run = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.vendorsHealth();
            setResult(r);
        } catch (err) {
            const status = err.response?.status;
            toast.error(status === 403
                ? "Vendor health is Commons Admin only"
                : (err.response?.data?.detail || "Vendor health check failed"));
        }
        setBusy(false);
    }, []);

    // iter85.5 — auto-run once on mount so the gate flips without clicks.
    useEffect(() => {
        if (autoRan.current) return;
        autoRan.current = true;
        run();
    }, [run]);

    // iter87.209 — report the verdict on its own effect, decoupled from
    // the one-shot fetch above (which costs 1 API-Sports request and
    // must not re-fire). If `activePromotion` is still loading when this
    // card mounts, `onVerdictResolved` is a stale no-op closure — this
    // effect re-fires whenever a fresh `onVerdictResolved` arrives (parent
    // re-renders with the loaded promotion) so the gate still flips.
    useEffect(() => {
        if (!result || !onVerdictResolved) return;
        const warns = result.checks.filter((c) => c.status === "warn").length;
        onVerdictResolved({ overall: result.overall, summary: { warn_count: warns } });
    }, [result, onVerdictResolved]);

    return (
        <CollapsibleCard
            testId="vendor-health-card"
            icon={Handshake}
            title="Vendor Health"
            badge={result ? result.overall : (busy ? "checking…" : "pending")}
            badgeClass={result ? (OVERALL_PILL[result.overall] || OVERALL_PILL.warn) : "bg-zinc-800 text-zinc-400 border-zinc-700"}
            verdict={result?.overall || null}
            headerExtra={
                <span className="flex items-center gap-1">
                    <WhatsThisButton
                        testId="vendor-health-whats-this"
                        title="Vendor Health"
                        what="A pre-deploy rollup of every third-party vendor we depend on: contract renewal radar, Resend sending-domain verification, and API-Sports request quotas (soccer + NFL + golf)."
                        lookFor="PASS. WARN means a vendor needs attention soon (e.g. renewal due, quota tightening) — waive with a reason. FAIL means something is actively broken (e.g. domain unverified, quota exhausted) and should block the commit until fixed."
                        notFor="Game data quality (Pre-deploy sweep) or engineering discipline (Values Engine)."
                    />
                    {result && (
                        <CopyDiagButton
                            testId="vendor-health-copy-diag-btn"
                            getText={() => buildDiagText({
                                card: "Vendor Health",
                                verdict: result.overall,
                                summary: `${result.checks.filter((c) => c.status === "pass").length}/${result.checks.length} pass`,
                                lines: result.checks
                                    .filter((c) => c.status !== "pass")
                                    .map((c) => diagLine(c.status, c.name, c.detail)),
                            })}
                        />
                    )}
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); if (!busy) run(); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); if (!busy) run(); } }}
                        data-testid="vendor-health-run-btn"
                        className={`brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
                    >
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                        {busy ? "Checking…" : "Run checks"}
                    </span>
                </span>
            }
        >
            <div className="space-y-3">
                <p className="text-[11px] text-zinc-500">
                    Renewal radar · Resend domain verification · API-Sports quotas (soccer + NFL). Manage vendors in
                    Compliance Ops → Third-Party Vendors.
                </p>
                <p className="text-[10px] text-zinc-600">
                    Auto-runs on load · pass approves the gate, warn auto-waives, fail blocks the commit until resolved or waived.
                </p>

                {result && (
                    <ul className="space-y-1.5" data-testid="vendor-health-checks">
                        {result.checks.map((c) => {
                            const meta = STATUS_META[c.status] || STATUS_META.warn;
                            const Icon = meta.icon;
                            return (
                                <li key={c.key} className="flex items-start gap-2 text-xs" data-testid={`vendor-health-${c.key}`}>
                                    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.cls}`} />
                                    <span className="font-bold text-zinc-300 shrink-0">{c.name}:</span>
                                    <span className="text-zinc-400">{c.detail}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {result?.golf_projection?.length > 0 && (
                    <div className="pt-1 border-t border-[#1c1c1c]" data-testid="vendor-health-golf-projection">
                        <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1">
                            Projected Slash Golf burn (this environment)
                        </p>
                        {result.golf_projection.map((g) => (
                            <p key={g.event_id} className="text-[10px] text-zinc-400" data-testid={`golf-projection-${g.event_id}`}>
                                <span className="font-bold text-zinc-300">{g.name}</span>
                                {" "}({g.start_date} → {g.end_date}) @ {g.cadence_label} cadence
                                {g.final_day_boost && ", 5-min final day"}
                                {" ≈ "}<span className="font-bold text-volt">{g.est_calls} calls</span>
                                {g.pct_of_quota != null && <> — {g.pct_of_quota}% of monthly quota</>}
                            </p>
                        ))}
                        <p className="text-[9px] text-zinc-600 mt-0.5">
                            Cache-mediated: one shared poller per bound event — tournaments, crews and templates read the
                            cached DB and add ZERO upstream calls. The estimate prices the cache-refresh cadence only.
                            Shared RapidAPI key — each environment (preview, production) refreshes its own cache, so total
                            burn = sum of both environments' projections.
                        </p>
                    </div>
                )}

                {result?.blast?.length > 0 && (
                    <div className="pt-1 border-t border-[#1c1c1c]" data-testid="vendor-health-blast">
                        <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1">
                            Blast radius (active tournaments)
                        </p>
                        {result.blast.map((b) => (
                            <p key={b.vendor} className="text-[10px] text-zinc-400" data-testid={`vendor-health-blast-${b.vendor.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                <span className="font-bold text-zinc-300">{b.vendor}</span>
                                {" — "}{b.tournaments} tournament(s) · {b.leagues} league(s)
                                {b.league_names?.length > 0 && `: ${b.league_names.join(", ")}`}
                            </p>
                        ))}
                    </div>
                )}
            </div>
        </CollapsibleCard>
    );
}
